<script setup>
import { Head, Link } from '@inertiajs/inertia-vue3';
import {defineComponent, h, nextTick, ref} from 'vue'
import LandingPageLayout from '@/Layouts/LandingPageLayout.vue';
import {
    ArrowPathIcon,
    InboxIcon,
    CloudArrowUpIcon,
    CogIcon,
    LockClosedIcon,
    ServerIcon,
    ShieldCheckIcon,
    SparklesIcon,
    UserGroupIcon,

    Bars3Icon, XMarkIcon as XMarkIconOutline
} from '@heroicons/vue/24/outline'

import { Dialog, DialogPanel, RadioGroup, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue'
import { CheckIcon, XMarkIcon as XMarkIconMini } from '@heroicons/vue/20/solid'

const pricing = {
    frequencies: [
        { value: 'monthly', label: 'Monthly' },
    ],
    tiers: [
        /*{
            name: 'DIY',
            id: 'tier-starter',
            href: '/register',
            featured: false,
            description: 'Access to all of our powerful tools to Do-It-Yourself.',
            price: { monthly: '$49.95' },
            mainFeatures: [
                'Advanced OCR',
                'Maintenance Tracking',
                'FAA Compliant Digital Logbooks',
                'AD Tear Sheet',
                'Share and Track Squawks',
            ],
        },*/
        {
            name: 'PLANELOGIX',
            id: 'tier-scale',
            href: '/register',
            featured: true,
            description: 'Access to our trained team to help you better manage your aircraft. This is our most popular plan and our best offering.',
            price: { monthly: '$99.95'},
            mainFeatures: [
                'All of the Tools We Offer',
                'Look-Aheads for More Efficient Maintenance',
                'Analyst Backed Support for New Records (billed at +$25/mo)',
                'Errors and Omissions Catching',
                'Add Value to Your Aircraft and Reduce Downtime',
            ],
        },
    ],
    sections: [
        {
            name: 'Features',
            features: [
                { name: 'Share Everything with Your A&P/IA', tiers: { DIY: true, PLANELOGIX: true } },
                { name: '100% Text Searchable Records', tiers: { DIY: true, PLANELOGIX: true } },
                { name: 'Instantly List Your Aircraft for Sale', tiers: { DIY: true, PLANELOGIX: true } },
                { name: 'Maintenance Tracking', tiers: { DIY: true, PLANELOGIX: true } },
                { name: 'Share and Track Squawks', tiers: { DIY: true, PLANELOGIX: true } },
                { name: 'Flight Log with Automatic FlightAware Updating', tiers: { DIY: true, PLANELOGIX: true } },
                { name: 'Powerful OCR and Search', tiers: { DIY: true, PLANELOGIX: true } },
                { name: 'FAA Compliant AC 120-78B E-Signatures', tiers: { DIY: true, PLANELOGIX: true } },
                { name: 'Secure and Redundant Logbook Backup', tiers: { DIY: true, PLANELOGIX: true } },
                { name: 'Export Your Data', tiers: { DIY: true, PLANELOGIX: true } },
                { name: 'Advanced AD Summary Report with Tear Sheet for Mechanics', tiers: { DIY: true, PLANELOGIX: true } },
            ],
        },
        {
            name: 'Full Document Support (billed monthly at $25/mo additional)',
            features: [
                { name: 'Use our professional transcription support team to handle unlimited new records', tiers: { DIY: false,  PLANELOGIX: true } },
                { name: 'Unlimited Document Organization by the PLANELOGIX team', tiers: { DIY: false, PLANELOGIX: true } },
                { name: 'Annual Printed Updates for your Executive Binder shipped to you', tiers: { DIY: false, PLANELOGIX: true } },
                { name: 'Analyst Backed Support for New Records', tiers: { PLANELOGIX: true } },
                { name: 'Priority E-mail Support', tiers: { PLANELOGIX: true } },
                { name: 'Insight Into Upcoming Annual via 30 and 60 Day Look-Ahead to Minimize Down Time', tiers: { PLANELOGIX: true } },
                { name: 'Documents (Work Orders, Invoices, FAA 337s, 8130s, STCs, etc…) associated with a maintenance record update will be appended to the source maintenance record for convenient retrieval', tiers: { PLANELOGIX: true } },
                { name: 'Adds value to your aircraft by having a third party professionally review your tracking and record keeping', tiers: { PLANELOGIX: true } },
            ],
        },
    ],
}

const faqs = [
    {
        id: 1,
        question: "What is the cost for you to onboard my original/historical logbooks and records and is it included in your monthly fees?",
        answer:
            "This cost will vary because every airplane is different, and it is billed separately as a one-time fee (exclusive of the monthly license/service fees shown above). Generally speaking older aircraft with more flight hours means more paperwork and maintenance history for our team to backup, hand type, and digitally organize for you. Since we price on a per tail basis, please <a class='underline text-indigo-600' href='/pricing?onboard_flow=true'>use this form</a> for a quick quote!",
    },
    {
        id: 2,
        question: "Do I have to use your onboarding service?",
        answer:
            "No! But like most things, it's a function of time or money. If you want your records professionally scanned, backed up, digitized, and hand typed, all in the most efficient, quickest, and professional way possible, using our team will be the best choice.",
    },
    {
        id: 3,
        question: "Can I download my data?",
        answer:
            "Yes, your data is always yours! With just one click you can download a .zip file that will contain everything in organized folders that make sense.",
    },
    {
        id: 4,
        question: "What kind of aircraft owners is this for?",
        answer:
            "We serve all of General Aviation! The majority of our customers are individual owner/operators, but we have a large customer base using their aircraft for business purposes.",
    },
    {
        id: 5,
        question: "How do I get you my logbooks?",
        answer:
            "The short answer is the way you feel most comfortable. Most customers utilize our secure shipment method, while some choose to fly to us (<a href='https://www.airnav.com/airport/krdu' target='_blank' class='underline text-gray-600'>KRDU</a>/<a href='https://www.airnav.com/airport/kbuy' target='_blank' class='underline text-gray-600'>KBUY</a>/<a href='https://www.airnav.com/airport/ktta' target='_blank' class='underline text-gray-600'>KTTA</a>). Others hire us to travel to them. We are happy to worth with you and if you decide to ship through us we utilize strict checklists and procedures (we are pilots ourselves!).",
    },
    // More questions...
]
const mobileMenuOpen = ref(false)
const frequency = ref(pricing.frequencies[0])

const showOnboardingForm = ref(false);

const openOnboardingForm = () => {
  showOnboardingForm.value = true;

};

import PrimaryButton from "@/Components/PrimaryButton.vue";

</script>

<template>
    <LandingPageLayout title="Pricing" :openOnboardingForm="showOnboardingForm" @onboardingFormClosed="showOnboardingForm = false">
        <!-- Digital only or Digital Hardcopy -->
            <main>
                <!-- Pricing section -->
                <div class="isolate overflow-hidden">
                    <div class="flow-root bg-gray-900 py-16 sm:pt-32 lg:pb-0">
                        <div class="mx-auto max-w-7xl px-6 lg:px-8">
                            <div class="relative z-10">
                                <h1 class="mx-auto max-w-4xl text-center text-5xl font-bold tracking-tight text-white">Simple pricing, no commitment</h1>
                                <p class="mx-auto mt-4 max-w-2xl text-center text-lg leading-8 text-white/60">Protect the value of your aircraft.</p>
                            </div>
                            <div class="relative mx-auto mt-10 grid max-w-md grid-cols-1 gap-y-8 lg:mx-0 lg:-mb-14 lg:max-w-none lg:grid-cols-1">
                                <svg viewBox="0 0 1208 1024" aria-hidden="true" class="absolute -bottom-48 left-1/2 h-[64rem] -translate-x-1/2 translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] lg:-top-48 lg:bottom-auto lg:translate-y-0">
                                    <ellipse cx="604" cy="512" fill="url(#d25c25d4-6d43-4bf9-b9ac-1842a30a4867)" rx="604" ry="512" />
                                    <defs>
                                        <radialGradient id="d25c25d4-6d43-4bf9-b9ac-1842a30a4867">
                                            <stop stop-color="#111827" />
                                            <stop offset="1" stop-color="#01b2ff" />
                                        </radialGradient>
                                    </defs>
                                </svg>
                                <div class="hidden lg:absolute lg:inset-x-px lg:bottom-0 lg:top-4 lg:block lg:rounded-t-2xl lg:bg-gray-800/80 lg:ring-1 lg:ring-white/10" aria-hidden="true" />
                                <div v-for="tier in pricing.tiers" :key="tier.id" :class="[tier.featured ? 'z-10 bg-white shadow-xl ring-1 ring-gray-900/10' : 'bg-gray-800/80 ring-1 ring-white/10 lg:bg-transparent lg:pb-14 lg:ring-0', 'relative rounded-2xl']">
                                    <div class="p-8 lg:pt-12 xl:p-10 xl:pt-14">
                                        <div class="flex items-center justify-between gap-x-4">
                                            <h2 :id="tier.id" :class="[tier.featured ? 'text-gray-900' : 'text-white', 'text-sm font-semibold leading-6']">{{ tier.name }}</h2>
                                            <p v-if="tier.name === 'PLANELOGIX'" class="rounded-full bg-indigo-600/10 px-2.5 py-1 text-xs font-semibold leading-5 text-indigo-600">Most popular</p>
                                        </div>
                                        <div class="lg:flex-col lg:items-stretch">
                                            <div class="mt-2 flex items-center gap-x-4">
                                                <p :class="[tier.featured ? 'text-gray-900' : 'text-white', 'text-4xl font-bold tracking-tight']">{{ tier.price[frequency.value] }}{{tier.name === 'PLANELOGIX' ? '*':''}}</p>
                                                <div class="text-sm leading-5">
                                                    <p :class="tier.featured ? 'text-gray-900' : 'text-white'">USD</p>
                                                    <p :class="tier.featured ? 'text-gray-500' : 'text-gray-400'">Billed monthly per tail</p>
                                                </div>
                                            </div>
                                            <small v-if="tier.name === 'PLANELOGIX'">*Starting price for single engine piston</small>
<!--                                          <a :href="tier.href" :aria-describedby="tier.id" :class="[tier.featured ? 'bg-indigo-600 shadow-sm hover:bg-indigo-500 focus-visible:outline-indigo-600' : 'bg-white/10 hover:bg-white/20 focus-visible:outline-white', 'rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2']">{{tier.name === 'DIY' ? 'Start Free Trial' : 'Sign Up'}}</a>-->
                                            <div class="mt-2 flex justify-center">
                                              <primary-button @click="openOnboardingForm()" class="mt-5 px-auto h-10 " :class="[tier.featured ? 'bg-indigo-600 shadow-sm hover:bg-indigo-500 focus-visible:outline-indigo-600' : 'bg-white/10 hover:bg-white/20 focus-visible:outline-white', 'rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2']">
                                              <span v-if="tier.name === 'DIY'" >
                                                <h3 class="text-center">Onboard Your Aircraft</h3>
                                              </span>
                                                <span v-else>
                                                <h3 class="text-center">Start your PLANELOGIX service <em><strong>TODAY</strong></em></h3>
                                              </span>

                                              </primary-button>
                                            </div>

                                        </div>
                                        <div class="mt-8 flow-root sm:mt-10">
                                            <ul role="list" :class="[tier.featured ? 'divide-gray-900/5 border-gray-900/5 text-gray-600' : 'divide-white/5 border-white/5 text-white', '-my-2 divide-y border-t text-sm leading-6 lg:border-t-0']">
                                                <li v-for="mainFeature in tier.mainFeatures" :key="mainFeature" class="flex gap-x-3 py-2">
                                                    <CheckIcon :class="[tier.featured ? 'text-indigo-600' : 'text-gray-500', 'h-6 w-5 flex-none']" aria-hidden="true" />
                                                    {{ mainFeature }}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="relative bg-gray-50 lg:pt-14">
                        <div class="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
                            <!-- Feature comparison (up to lg) -->
                            <section aria-labelledby="mobile-comparison-heading" class="lg:hidden">
                                <h2 id="mobile-comparison-heading" class="sr-only">Feature comparison</h2>

                                <div class="mx-auto max-w-2xl space-y-16">
                                    <div v-for="tier in pricing.tiers" :key="tier.id" class="border-t border-gray-900/10">
                                        <div :class="[tier.featured ? 'border-indigo-600' : 'border-transparent', '-mt-px w-72 border-t-2 pt-10 md:w-80']">
                                            <h3 :class="[tier.featured ? 'text-indigo-600' : 'text-gray-900', 'text-sm font-semibold leading-6']">{{ tier.name }}</h3>
                                            <p class="mt-1 text-sm leading-6 text-gray-600">{{ tier.description }}</p>
                                        </div>

                                        <div class="mt-10 space-y-10">
                                            <div v-for="section in pricing.sections" :key="section.name">
                                                <h4 class="text-sm font-semibold leading-6 text-gray-900">{{ section.name }}</h4>
                                                <div class="relative mt-6">
                                                    <!-- Fake card background -->
                                                    <div aria-hidden="true" class="absolute inset-y-0 right-0 hidden w-1/2 rounded-lg bg-white shadow-sm sm:block" />

                                                    <div :class="[tier.featured ? 'ring-2 ring-indigo-600' : 'ring-1 ring-gray-900/10', 'relative rounded-lg bg-white shadow-sm sm:rounded-none sm:bg-transparent sm:shadow-none sm:ring-0']">
                                                        <dl class="divide-y divide-gray-200 text-sm leading-6">
                                                            <div v-for="feature in section.features" :key="feature.name" class="flex items-center justify-between px-4 py-3 sm:grid sm:grid-cols-2 sm:px-0">
                                                                <dt class="pr-4 text-gray-600">{{ feature.name }}</dt>
                                                                <dd class="flex items-center justify-end sm:justify-center sm:px-4">
                                                                    <span v-if="typeof feature.tiers[tier.name] === 'string'" :class="tier.featured ? 'font-semibold text-indigo-600' : 'text-gray-900'">{{ feature.tiers[tier.name] }}</span>
                                                                    <template v-else>
                                                                        <CheckIcon v-if="feature.tiers[tier.name] === true" class="mx-auto h-5 w-5 text-indigo-600" aria-hidden="true" />
                                                                        <XMarkIconMini v-else class="mx-auto h-5 w-5 text-gray-400" aria-hidden="true" />
                                                                        <span class="sr-only">{{ feature.tiers[tier.name] === true ? 'Yes' : 'No' }}</span>
                                                                    </template>
                                                                </dd>
                                                            </div>
                                                        </dl>
                                                    </div>

                                                    <!-- Fake card border -->
                                                    <div aria-hidden="true" :class="[tier.featured ? 'ring-2 ring-indigo-600' : 'ring-1 ring-gray-900/10', 'pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 rounded-lg sm:block']" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <!-- Feature comparison (lg+) -->
                            <section aria-labelledby="comparison-heading" class="hidden lg:block">
                                <h2 id="comparison-heading" class="sr-only">Feature comparison</h2>

                                <div class="grid grid-cols-2 gap-x-8 border-t border-gray-900/10 before:block">
                                    <div v-for="tier in pricing.tiers" :key="tier.id" aria-hidden="true" class="-mt-px">
                                        <div :class="[tier.featured ? 'border-indigo-600' : 'border-transparent', 'border-t-2 pt-10']">
                                            <p :class="[tier.featured ? 'text-indigo-600' : 'text-gray-900', 'text-sm font-semibold leading-6']">{{ tier.name }}</p>
                                            <p class="mt-1 text-sm leading-6 text-gray-600">{{ tier.description }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="-mt-6 space-y-16">
                                    <div v-for="section in pricing.sections" :key="section.name">
                                        <h3 class="text-sm font-semibold leading-6 text-gray-900">{{ section.name }}</h3>
                                        <div class="relative -mx-8 mt-10">
                                            <!-- Fake card backgrounds -->
                                            <div class="absolute inset-x-8 inset-y-0 grid grid-cols-2 gap-x-8 before:block" aria-hidden="true">
                                                <!--<div class="h-full w-full rounded-lg bg-white shadow-sm" />-->
                                                <div class="h-full w-full rounded-lg bg-white shadow-sm" />
                                            </div>

                                            <table class="relative w-full border-separate border-spacing-x-8">
                                                <thead>
                                                <tr class="text-left">
                                                    <th scope="col">
                                                        <span class="sr-only">Feature</span>
                                                    </th>
                                                    <th v-for="tier in pricing.tiers" :key="tier.id" scope="col">
                                                        <span class="sr-only">{{ tier.name }} tier</span>
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                <tr v-for="(feature, featureIdx) in section.features" :key="feature.name">
                                                    <th scope="row" class="w-1/4 py-3 pr-4 text-left text-sm font-normal leading-6 text-gray-900">
                                                        {{ feature.name }}
                                                        <div v-if="featureIdx !== section.features.length - 1" class="absolute inset-x-8 mt-3 h-px bg-gray-200" />
                                                    </th>
                                                    <td v-for="tier in pricing.tiers" :key="tier.id" class="relative w-1/4 px-4 py-0 text-center">
                                                        <span class="relative h-full w-full py-3">
                                                          <span v-if="typeof feature.tiers[tier.name] === 'string'" :class="[tier.featured ? 'font-semibold text-indigo-600' : 'text-gray-900', 'text-sm leading-6']">{{ feature.tiers[tier.name] }}</span>
                                                          <template v-else>
                                                            <CheckIcon v-if="feature.tiers[tier.name] === true" class="mx-auto h-5 w-5 text-indigo-600" aria-hidden="true" />
                                                            <XMarkIconMini v-else class="mx-auto h-5 w-5 text-gray-400" aria-hidden="true" />
                                                            <span class="sr-only">{{ feature.tiers[tier.name] === true ? 'Yes' : 'No' }}</span>
                                                          </template>
                                                        </span>
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>

                                            <!-- Fake card borders -->
                                            <div class="pointer-events-none absolute inset-x-8 inset-y-0 grid grid-cols-2 gap-x-8 before:block" aria-hidden="true">
                                                <div v-for="tier in pricing.tiers" :key="tier.id" :class="[tier.featured ? 'ring-2 ring-indigo-600' : 'ring-1 ring-gray-900/10', 'rounded-lg']" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>

                <!-- FAQ section -->
                <div class="mx-auto mt-24 max-w-7xl divide-y divide-gray-900/10 px-6 sm:mt-56 lg:px-8 mb-10">
                    <h2 class="text-2xl font-bold leading-10 tracking-tight text-gray-900">Frequently asked questions</h2>
                    <dl class="mt-10 space-y-8 divide-y divide-gray-900/10">
                        <div v-for="faq in faqs" :key="faq.id" class="pt-8 lg:grid lg:grid-cols-12 lg:gap-8">
                            <dt class="text-base font-semibold leading-7 text-gray-900 lg:col-span-5">{{ faq.question }}</dt>
                            <dd class="mt-4 lg:col-span-7 lg:mt-0">
                                <p class="text-base leading-7 text-gray-600" v-html="faq.answer"></p>
                            </dd>
                        </div>
                    </dl>
                </div>
            </main>

    </LandingPageLayout>


</template>
